/// <reference types="@webgpu/types" />

import type { CameraState, Size } from '../../types';
import type { SurfaceMesh } from '../../geometry/surfaceMesh';
import type { ColorScale } from '../colorScale';
import { DEFAULT_LIGHTING, type LightingModel } from '../lighting';
import type { DrapedGrid } from '../drapedGrid';
import type { OutlineInk } from '../outlines';
import type { SurfacePaint } from '../surfacePaint';
import { bakeRampTexture, toRampTextureWidth } from './rampTexture';
import { OUTLINE_SHADER, SURFACE_SHADER } from './shaders';
import { packSurfaceUniforms, UNIFORM_FLOAT_COUNT } from './uniforms';

/**
 * Whether this browser can run the hardware path at all.
 *
 * Checked before anything is allocated, because WebGPU is still not universal - enterprise browsers
 * in particular lag - so the software renderer is not a nicety, it is the path a real share of users
 * will take. Asking for an adapter is part of the check rather than only feature-detecting
 * `navigator.gpu`: the API can be present while no adapter is available, on a machine with a
 * blocklisted driver or inside a virtualised environment.
 */
export const requestGpuDevice = async (): Promise<GPUDevice | null> => {
  if (typeof navigator === 'undefined' || !navigator.gpu) return null;

  try {
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) return null;
    return await adapter.requestDevice();
  } catch {
    /* Swallowed deliberately: every failure here means the same thing to the caller, which is that
       the software path must be used. Distinguishing them would only add branches nobody acts on. */
    return null;
  }
};

/** Bytes per vertex: 3 position floats, 3 normal floats, 1 value float. */
const VERTEX_STRIDE = 7 * 4;

const FLOATS_PER_VERTEX = 7;

export type SurfaceGpuRenderer = {
  /** Uploads a new mesh. Cheap to call only when the mesh actually changes. */
  setMesh: (mesh: SurfaceMesh) => void;
  /** Rebakes the ramp. Separate from the mesh, since colour settings change independently. */
  setColorScale: (colorScale: ColorScale) => void;
  /** Uploads the paint that colors the surface in place of the ramp; null colors it by height. */
  setPaint: (paint: SurfacePaint | null) => void;
  draw: (args: SurfaceGpuDrawArgs) => void;
  /** Releases every GPU allocation. Must be called, or the device leaks buffers per chart. */
  destroy: () => void;
};

export type SurfaceGpuDrawArgs = {
  camera: CameraState;
  viewport: Size;
  lighting?: LightingModel;
  shading: number;
  zRange: readonly [number, number];
  /** Cleared to this before drawing, so the chart's own surface colour shows through. */
  clearColor: { r: number; g: number; b: number; a: number };
  /** Axis grid lines drawn onto the surface; null draws none. */
  drapedGrid: DrapedGrid | null;
  /** Bands each light's diffuse term is rounded to, for cel shading; 0 leaves it smooth. */
  celSteps: number;
  /** Inks the silhouette and occluding contours over the surface; null draws none. */
  outline: OutlineInk | null;
};

export type CreateSurfaceGpuRendererArgs = {
  device: GPUDevice;
  canvas: HTMLCanvasElement;
  colorScale: ColorScale;
};

/**
 * Sets up the pipeline once and holds the allocations that outlive a frame.
 *
 * Split this way because the expensive things have different lifetimes: the pipeline and the sampler
 * are per chart, the vertex and index buffers change when the mesh does, the ramp texture when the
 * colour settings do, and only the uniform buffer is written every frame. Rebuilding any of the
 * others per frame is the usual way a GPU chart ends up slower than a canvas one.
 */
export const createSurfaceGpuRenderer = ({
  device,
  canvas,
  colorScale,
}: CreateSurfaceGpuRendererArgs): SurfaceGpuRenderer => {
  const context = canvas.getContext('webgpu');
  if (!context) {
    throw new Error('WebGPU canvas context unavailable');
  }

  const format = navigator.gpu.getPreferredCanvasFormat();
  context.configure({ device, format, alphaMode: 'premultiplied' });

  const module = device.createShaderModule({ code: SURFACE_SHADER });

  const pipeline = device.createRenderPipeline({
    layout: 'auto',
    vertex: {
      module,
      entryPoint: 'vertexMain',
      buffers: [
        {
          arrayStride: VERTEX_STRIDE,
          attributes: [
            { shaderLocation: 0, offset: 0, format: 'float32x3' },
            { shaderLocation: 1, offset: 12, format: 'float32x3' },
            { shaderLocation: 2, offset: 24, format: 'float32' },
          ],
        },
      ],
    },
    fragment: {
      module,
      entryPoint: 'fragmentMain',
      targets: [{ format }],
    },
    primitive: {
      topology: 'triangle-list',
      cullMode: 'none',
    },
    depthStencil: {
      format: 'depth24plus',
      depthWriteEnabled: true,
      /* Nearer fragments carry smaller clip z, which the vertex shader arranges. */
      depthCompare: 'less',
    },
  });

  const uniformBuffer = device.createBuffer({
    size: UNIFORM_FLOAT_COUNT * 4,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  const outlineModule = device.createShaderModule({ code: OUTLINE_SHADER });
  /** One full-screen triangle over the finished surface, reading its depth and blending ink over. */
  const outlinePipeline = device.createRenderPipeline({
    layout: 'auto',
    vertex: { module: outlineModule, entryPoint: 'outlineVertex' },
    fragment: {
      module: outlineModule,
      entryPoint: 'outlineFragment',
      targets: [
        {
          format,
          blend: {
            color: {
              srcFactor: 'one',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add',
            },
            alpha: {
              srcFactor: 'one',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add',
            },
          },
        },
      ],
    },
    primitive: { topology: 'triangle-list' },
  });
  const inkBuffer = device.createBuffer({
    size: 16,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  /* Clamped, so a value at either end of the range samples the true end of the ramp rather than
     wrapping to the opposite extreme. */
  const clamped = {
    addressModeU: 'clamp-to-edge',
    addressModeV: 'clamp-to-edge',
  } as const;

  /* Two samplers, chosen per scale. A continuous ramp wants the blend; a banded one does not - a
     band edge is a step, and linear filtering turns it into a one-texel gradient, which is exactly
     what banding was added to avoid. Built once and selected in `setColorScale`, where the bind
     group is rebuilt anyway. */
  const smoothSampler = device.createSampler({
    ...clamped,
    magFilter: 'linear',
    minFilter: 'linear',
  });
  const steppedSampler = device.createSampler({
    ...clamped,
    magFilter: 'nearest',
    minFilter: 'nearest',
  });

  let rampTexture: GPUTexture | null = null;
  let rampSamplerInUse: GPUSampler = smoothSampler;
  /* A texel of nothing stands in while the surface is colored by height, since the bind group
     has to hold some texture at the paint's binding. */
  const placeholderPaint = device.createTexture({
    size: { width: 1, height: 1 },
    format: 'rgba8unorm',
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
  });
  let paintTexture: GPUTexture | null = null;
  let bindGroup: GPUBindGroup | null = null;
  let vertexBuffer: GPUBuffer | null = null;
  let indexBuffer: GPUBuffer | null = null;
  let indexCount = 0;
  /* What the buffers in hand were built from, so a mesh that changed only its normals can be
     recognised without comparing any contents. */
  let uploadedIndices: Uint32Array | null = null;
  let uploadedVertexCount = 0;
  let interleaved: Float32Array | null = null;
  let depthTexture: GPUTexture | null = null;
  let outlineBindGroup: GPUBindGroup | null = null;
  let depthSize: Size = { width: 0, height: 0 };

  const setColorScale = (scale: ColorScale): void => {
    rampTexture?.destroy();
    const rampWidth = toRampTextureWidth(scale);
    rampTexture = device.createTexture({
      size: { width: rampWidth, height: 1 },
      format: 'rgba8unorm',
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    });
    device.queue.writeTexture(
      { texture: rampTexture },
      bakeRampTexture(scale),
      { bytesPerRow: rampWidth * 4 },
      { width: rampWidth, height: 1 }
    );

    rampSamplerInUse = scale.bands > 0 ? steppedSampler : smoothSampler;
    rebindTextures();
  };

  const rebindTextures = (): void => {
    if (!rampTexture) return;
    bindGroup = device.createBindGroup({
      layout: pipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: uniformBuffer } },
        { binding: 1, resource: rampTexture.createView() },
        { binding: 2, resource: rampSamplerInUse },
        {
          binding: 3,
          resource: (paintTexture ?? placeholderPaint).createView(),
        },
        { binding: 4, resource: smoothSampler },
      ],
    });
  };

  const setPaint = (paint: SurfacePaint | null): void => {
    paintTexture?.destroy();
    paintTexture = null;
    if (paint) {
      paintTexture = device.createTexture({
        size: { width: paint.width, height: paint.height },
        format: 'rgba8unorm',
        usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
      });
      device.queue.writeTexture(
        { texture: paintTexture },
        paint.data,
        { bytesPerRow: paint.width * 4 },
        { width: paint.width, height: paint.height }
      );
    }
    rebindTextures();
  };

  /**
   * Whether the buffers in hand already describe this mesh's topology.
   *
   * Keyed on the identity of the index array rather than on its contents, which is exactly the
   * signal `rescaleNormals` gives: it shares `positions`, `values` and `indices` with the mesh it
   * rescaled and replaces only `normals`. A relief drag is the case that matters - it reaches here
   * every frame, and rebuilding destroyed both buffers, re-interleaved every vertex and
   * re-uploaded a bit-identical index buffer to change three floats in seven.
   */
  const hasSameTopology = (mesh: SurfaceMesh): boolean =>
    vertexBuffer !== null &&
    indexBuffer !== null &&
    uploadedIndices === mesh.indices &&
    uploadedVertexCount === mesh.vertexCount;

  const setMesh = (mesh: SurfaceMesh): void => {
    const reuse = hasSameTopology(mesh);

    if (!reuse) {
      vertexBuffer?.destroy();
      indexBuffer?.destroy();
      vertexBuffer = null;
      indexBuffer = null;
      indexCount = 0;
      uploadedIndices = null;
      uploadedVertexCount = 0;
    }
    if (mesh.triangleCount === 0) {
      interleaved = null;
      return;
    }

    /* Interleaved into one buffer rather than three: one bound stream and one stride, and the
       attributes a fragment needs together sit together in memory. */
    const floats = mesh.vertexCount * FLOATS_PER_VERTEX;
    if (!interleaved || interleaved.length !== floats) {
      interleaved = new Float32Array(floats);
    }
    for (let index = 0; index < mesh.vertexCount; index++) {
      const source = index * 3;
      const target = index * FLOATS_PER_VERTEX;
      interleaved[target] = mesh.positions[source];
      interleaved[target + 1] = mesh.positions[source + 1];
      interleaved[target + 2] = mesh.positions[source + 2];
      interleaved[target + 3] = mesh.normals[source];
      interleaved[target + 4] = mesh.normals[source + 1];
      interleaved[target + 5] = mesh.normals[source + 2];
      interleaved[target + 6] = mesh.values[index];
    }

    if (reuse && vertexBuffer) {
      /* The vertices are rewritten because the normals sit between the positions and the value in
         each stride, so there is no contiguous run to write on its own. The indices are not
         touched at all and neither buffer is reallocated - which is the churn this path exists to
         avoid, rather than the write itself. */
      device.queue.writeBuffer(vertexBuffer, 0, interleaved);
      return;
    }

    vertexBuffer = device.createBuffer({
      size: interleaved.byteLength,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(vertexBuffer, 0, interleaved);

    indexBuffer = device.createBuffer({
      size: mesh.indices.byteLength,
      usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
    });
    device.queue.writeBuffer(indexBuffer, 0, mesh.indices);
    indexCount = mesh.indices.length;
    uploadedIndices = mesh.indices;
    uploadedVertexCount = mesh.vertexCount;
  };

  /** The depth attachment, and the outline pass's binding of it, rebuilt together on a resize. */
  const ensureDepthTexture = (
    viewport: Size
  ): { depth: GPUTexture; outlineBindGroup: GPUBindGroup } => {
    if (
      depthTexture &&
      outlineBindGroup &&
      depthSize.width === viewport.width &&
      depthSize.height === viewport.height
    ) {
      return { depth: depthTexture, outlineBindGroup };
    }

    depthTexture?.destroy();
    depthTexture = device.createTexture({
      size: { width: viewport.width, height: viewport.height },
      format: 'depth24plus',
      usage:
        GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    outlineBindGroup = device.createBindGroup({
      layout: outlinePipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: depthTexture.createView() },
        { binding: 1, resource: { buffer: inkBuffer } },
      ],
    });
    depthSize = { ...viewport };
    return { depth: depthTexture, outlineBindGroup };
  };

  setColorScale(colorScale);

  return {
    setMesh,
    setColorScale,
    setPaint,

    draw: ({
      camera,
      viewport,
      lighting = DEFAULT_LIGHTING,
      shading,
      zRange,
      clearColor,
      drapedGrid,
      outline,
      celSteps,
    }) => {
      if (!vertexBuffer || !indexBuffer || !bindGroup || indexCount === 0) {
        return;
      }
      if (viewport.width === 0 || viewport.height === 0) return;

      device.queue.writeBuffer(
        uniformBuffer,
        0,
        packSurfaceUniforms({
          camera,
          viewport,
          lighting,
          shading,
          zRange,
          drapedGrid,
          painted: paintTexture !== null,
          celSteps,
        })
      );

      const attachments = ensureDepthTexture(viewport);
      const target = context.getCurrentTexture();
      const encoder = device.createCommandEncoder();
      const pass = encoder.beginRenderPass({
        colorAttachments: [
          {
            view: target.createView(),
            clearValue: clearColor,
            loadOp: 'clear',
            storeOp: 'store',
          },
        ],
        depthStencilAttachment: {
          view: attachments.depth.createView(),
          /* Cleared to 1, the far plane, since nearer fragments carry smaller z. */
          depthClearValue: 1,
          depthLoadOp: 'clear',
          depthStoreOp: 'store',
        },
      });

      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bindGroup);
      pass.setVertexBuffer(0, vertexBuffer);
      pass.setIndexBuffer(indexBuffer, 'uint32');
      pass.drawIndexed(indexCount);
      pass.end();

      /* A pass of its own, because the depth it reads is the attachment the surface pass wrote. */
      if (outline && outline.opacity > 0) {
        const [red, green, blue] = outline.color;
        device.queue.writeBuffer(
          inkBuffer,
          0,
          new Float32Array([
            red / 255,
            green / 255,
            blue / 255,
            outline.opacity,
          ])
        );
        const outlinePass = encoder.beginRenderPass({
          colorAttachments: [
            { view: target.createView(), loadOp: 'load', storeOp: 'store' },
          ],
        });
        outlinePass.setPipeline(outlinePipeline);
        outlinePass.setBindGroup(0, attachments.outlineBindGroup);
        outlinePass.draw(3);
        outlinePass.end();
      }

      device.queue.submit([encoder.finish()]);
    },

    destroy: () => {
      vertexBuffer?.destroy();
      indexBuffer?.destroy();
      rampTexture?.destroy();
      paintTexture?.destroy();
      placeholderPaint.destroy();
      depthTexture?.destroy();
      uniformBuffer.destroy();
      inkBuffer.destroy();
    },
  };
};

export type ShaderDiagnostic = {
  type: 'error' | 'warning' | 'info';
  lineNumber: number;
  message: string;
};

/**
 * Compiles the shader and returns whatever the driver says about it.
 *
 * Separate from creating a renderer because WGSL compilation is asynchronous and its diagnostics are
 * not thrown - a shader with an error compiles to a module that simply draws nothing, so without
 * asking for the messages a mistake looks identical to an empty mesh. Reported rather than logged so
 * a caller can put the text on screen, which is the only way to read it on a machine that cannot run
 * the shader at all.
 */
export const getShaderDiagnostics = async (
  device: GPUDevice
): Promise<ShaderDiagnostic[]> => {
  const module = device.createShaderModule({ code: SURFACE_SHADER });
  const info = await module.getCompilationInfo();

  return info.messages.map(({ type, lineNum, message }) => ({
    type,
    lineNumber: lineNum,
    message,
  }));
};
